import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomList, CustomListCreate, CustomListUpdate, FilterCriteria, Comic, Tag, Language } from '../../types';
import { createCustomList, updateCustomList, generateCustomListShareToken, regenerateCustomListShareToken } from '../../api';
import { XMarkIcon, FunnelIcon, ListBulletIcon, GlobeAltIcon, LinkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import TagAutocomplete from '../autocomplete/TagAutocomplete';
import ComicAutocomplete from '../autocomplete/ComicAutocomplete';
import { useWebroot } from '../../hooks';
import { getPublicListUrl } from '../../utils';

interface ListEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onListSaved: (savedListId?: number) => void;
  list?: CustomList | null;
}

const ListEditor: React.FC<ListEditorProps> = ({ isOpen, onClose, onListSaved, list }) => {
  const { t } = useTranslation();
  const { webroot } = useWebroot();
  const [formData, setFormData] = useState<CustomListCreate>({
    name: '',
    description: '',
    list_type: 'manual',
    is_public: false,
    is_draft: false,
    filter_criteria: {
      include_tags: [],
      exclude_tags: [],
      include_statuses: [],
      exclude_statuses: [],
      include_languages: [],
      exclude_languages: [],
      is_adult_content: undefined,
    },
    comic_ids: [],
  });

  const [selectedTags, setSelectedTags] = useState<{ include: Tag[]; exclude: Tag[] }>({
    include: [],
    exclude: [],
  });
  const [selectedLanguages, setSelectedLanguages] = useState<{ include: Language[]; exclude: Language[] }>({
    include: [],
    exclude: [],
  });
  const [selectedComics, setSelectedComics] = useState<Comic[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'content'>('basic');
  const [shareUrl, setShareUrl] = useState<string>('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [draftList, setDraftList] = useState<CustomList | null>(null);

  const isEditMode = !!list;



  useEffect(() => {
    if (isOpen) {
      // Reset save status when modal opens
      
      if (isEditMode && list) {
        // Populate form with existing list data
        setFormData({
          name: list.name,
          description: list.description || '',
          list_type: list.list_type,
          is_public: list.is_public,
          is_draft: list.is_draft,
          filter_criteria: list.filter_criteria || {
            include_tags: [],
            exclude_tags: [],
            include_statuses: [],
            exclude_statuses: [],
            include_languages: [],
            exclude_languages: [],
            is_adult_content: undefined,
          },
          comic_ids: list.comics.map(c => c.id),
        });
        setSelectedComics(list.comics);
        if (list.share_token) {
          setShareUrl(getPublicListUrl(list.share_token, webroot));
        }
        // TODO: Load selected tags, publishers, languages from IDs
      } else {
        // Create draft list immediately for new lists
        const createDraft = async () => {
          try {
            const draftData: CustomListCreate = {
              name: 'Untitled List',
              description: '',
              list_type: 'manual',
              is_public: false,
              is_draft: true,
              filter_criteria: {
                include_tags: [],
                exclude_tags: [],
                include_statuses: [],
                exclude_statuses: [],
                include_languages: [],
                exclude_languages: [],
                is_adult_content: undefined,
              },
              comic_ids: [],
            };
            const draft = await createCustomList(draftData);
            setDraftList(draft);
            
            // Reset form for new list
            setFormData({
              name: '',
              description: '',
              list_type: 'manual',
              is_public: false,
              is_draft: false,
              filter_criteria: {
                include_tags: [],
                exclude_tags: [],
                include_statuses: [],
                exclude_statuses: [],
                include_languages: [],
                exclude_languages: [],
                is_adult_content: undefined,
              },
              comic_ids: [],
            });
          } catch (error) {
            
          }
        };
        
        if (!draftList) {
          createDraft();
        }
        
        setSelectedComics([]);
        setSelectedTags({ include: [], exclude: [] });
        setSelectedLanguages({ include: [], exclude: [] });
        setShareUrl('');
      }
    }
  }, [isOpen, isEditMode, list, webroot, draftList]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Update filter criteria with selected items
      const updatedFilterCriteria: FilterCriteria = {
        ...formData.filter_criteria!,
        include_tags: selectedTags.include.map(t => t.id),
        exclude_tags: selectedTags.exclude.map(t => t.id),
        include_languages: selectedLanguages.include.map(l => l.id),
        exclude_languages: selectedLanguages.exclude.map(l => l.id),
      };

      let savedList: CustomList;
      
      if (isEditMode && list) {
        const updateData: CustomListUpdate = {
          name: formData.name,
          description: formData.description,
          list_type: formData.list_type, // Allow changing list type for existing lists
          is_public: formData.is_public,
          filter_criteria: formData.list_type === 'dynamic' ? updatedFilterCriteria : undefined,
          comic_ids: formData.list_type === 'manual' ? selectedComics.map(c => c.id) : undefined,
        };
        savedList = await updateCustomList(list.id, updateData);
      } else if (draftList) {
        // Update the draft list with final data and publish it
        const updateData: CustomListUpdate = {
          name: formData.name,
          description: formData.description,
          is_public: formData.is_public,
          is_draft: false, // Convert draft to published list
          list_type: formData.list_type, // Update list type (manual -> dynamic or vice versa)
          filter_criteria: formData.list_type === 'dynamic' ? updatedFilterCriteria : undefined,
          comic_ids: formData.list_type === 'manual' ? selectedComics.map(c => c.id) : undefined,
        };
        savedList = await updateCustomList(draftList.id, updateData);
        setDraftList(null); // Clear draft since it's now permanent
      } else {
        const createData: CustomListCreate = {
          ...formData,
          is_draft: false,
          filter_criteria: formData.list_type === 'dynamic' ? updatedFilterCriteria : undefined,
          comic_ids: formData.list_type === 'manual' ? selectedComics.map(c => c.id) : [],
        };
        savedList = await createCustomList(createData);
      }

      // Update share URL if the list is public and has a share token
      if (savedList.is_public && savedList.share_token) {
        setShareUrl(getPublicListUrl(savedList.share_token, webroot));
      } else {
        setShareUrl('');
      }

      // Mark as successfully saved and clear draft reference
      setDraftList(null); // Clear draft reference immediately after save
      
      
      
      onListSaved(savedList.id);
      
      // Close immediately after save for all lists
      setShareUrl('');
      setDraftList(null);
      onClose();
    } catch (error) {
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusToggle = (status: 'owned' | 'wishlist' | 'ordered' | 'loaned', type: 'include' | 'exclude') => {
    setFormData(prev => {
      const criteria = prev.filter_criteria!;
      const statusKey = `${type}_statuses` as 'include_statuses' | 'exclude_statuses';
      const currentStatuses = criteria[statusKey];
      
      return {
        ...prev,
        filter_criteria: {
          ...criteria,
          [statusKey]: currentStatuses.includes(status)
            ? currentStatuses.filter(s => s !== status)
            : [...currentStatuses, status],
        },
      };
    });
  };

  const copyShareUrl = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      // TODO: Show toast notification
    }
  };

  const regenerateShareUrl = async () => {
    if (!isEditMode || !list) return;
    
    setIsRegenerating(true);
    try {
      const updatedList = await regenerateCustomListShareToken(list.id);
      if (updatedList.share_token) {
        setShareUrl(getPublicListUrl(updatedList.share_token, webroot));
      }
    } catch (error) {
      
    } finally {
      setIsRegenerating(false);
    }
  };

  const generatePlaceholderUrl = () => {
    // Return the current shareUrl which now includes preview URLs
    return shareUrl;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden border dark:border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {isEditMode ? t('list.editList') : t('list.createList')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-300 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'basic'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t('listEditor.basicSettings')}
          </button>
          <button
            onClick={() => setActiveTab('content')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'content'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t('listEditor.content')}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('listEditor.listName')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('listEditor.description')}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('listEditor.listType')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div
                    onClick={() => setFormData(prev => ({ ...prev, list_type: 'manual' }))}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.list_type === 'manual'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <ListBulletIcon className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{t('listEditor.manualList')}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('listEditor.manualListDescription')}</p>
                      </div>
                    </div>
                  </div>
                  <div
                    onClick={() => setFormData(prev => ({ ...prev, list_type: 'dynamic' }))}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.list_type === 'dynamic'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <FunnelIcon className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{t('listEditor.dynamicList')}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('listEditor.dynamicListDescription')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={formData.is_public}
                  onChange={async (e) => {
                    const isPublic = e.target.checked;
                    setFormData(prev => ({ ...prev, is_public: isPublic }));
                    
                    if (isPublic) {
                      const targetList = isEditMode ? list! : draftList;
                      if (targetList) {
                        // Generate token immediately without making public
                        try {
                          const updatedList = await generateCustomListShareToken(targetList.id);
                          if (updatedList.share_token) {
                            setShareUrl(getPublicListUrl(updatedList.share_token, webroot));
                          }
                        } catch (error) {
                          
                        }
                      }
                    } else {
                      // Making list private - just clear the URL, don't update backend yet
                      setShareUrl('');
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label htmlFor="is_public" className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <GlobeAltIcon className="h-4 w-4" />
                  <span>{t('listEditor.makeListPublic')}</span>
                </label>
              </div>

              {formData.is_public && (
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('common.shareUrl')}
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={generatePlaceholderUrl()}
                      readOnly
                      className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm"
                    />
                    <button
                      type="button"
                      onClick={copyShareUrl}
                      disabled={!shareUrl}
                      className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      title={t('common.copyUrl')}
                    >
                      <LinkIcon className="h-4 w-4" />
                    </button>
                    {isEditMode && shareUrl && (
                      <button
                        type="button"
                        onClick={regenerateShareUrl}
                        disabled={isRegenerating}
                        className="px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                        title={t('listEditor.regenerateUrl')}
                      >
                        <ArrowPathIcon className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                  </div>
                  {!shareUrl && !isEditMode && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {t('listEditor.urlWillBeGenerated')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'content' && (
            <div className="space-y-6">
              {formData.list_type === 'manual' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('listEditor.selectComics')}
                  </label>
                  <ComicAutocomplete
                    selectedComics={selectedComics}
                    onComicsChange={setSelectedComics}
                    placeholder={t('placeholder.searchAndSelectComics')}
                  />
                </div>
              )}

              {formData.list_type === 'dynamic' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('listEditor.filterCriteria')}</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('comic.tags')}
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('listEditor.includeTags')}</label>
                            <TagAutocomplete
                              selectedTags={selectedTags.include}
                              onTagSelect={(tag) => setSelectedTags(prev => ({ 
                                ...prev, 
                                include: [...prev.include, tag] 
                              }))}
                              onTagRemove={(tagId) => setSelectedTags(prev => ({ 
                                ...prev, 
                                include: prev.include.filter(t => t.id !== tagId) 
                              }))}
                              placeholder={t('placeholder.includeTags')}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{t('listEditor.excludeTags')}</label>
                            <TagAutocomplete
                              selectedTags={selectedTags.exclude}
                              onTagSelect={(tag) => setSelectedTags(prev => ({ 
                                ...prev, 
                                exclude: [...prev.exclude, tag] 
                              }))}
                              onTagRemove={(tagId) => setSelectedTags(prev => ({ 
                                ...prev, 
                                exclude: prev.exclude.filter(t => t.id !== tagId) 
                              }))}
                              placeholder={t('placeholder.excludeTags')}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('comic.status')}
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">{t('listEditor.includeStatuses')}</label>
                            <div className="flex flex-wrap gap-2">
                              {(['owned', 'wishlist', 'ordered', 'loaned'] as const).map(status => (
                                <button
                                  key={status}
                                  type="button"
                                  onClick={() => handleStatusToggle(status, 'include')}
                                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                    formData.filter_criteria!.include_statuses.includes(status)
                                      ? 'bg-green-100 text-green-800 border-green-300'
                                      : 'bg-gray-100 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                                  } border`}
                                >
                                  {t(`status.${status}`)}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">{t('listEditor.excludeStatuses')}</label>
                            <div className="flex flex-wrap gap-2">
                              {(['owned', 'wishlist', 'ordered', 'loaned'] as const).map(status => (
                                <button
                                  key={status}
                                  type="button"
                                  onClick={() => handleStatusToggle(status, 'exclude')}
                                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                    formData.filter_criteria!.exclude_statuses.includes(status)
                                      ? 'bg-red-100 text-red-800 border-red-300'
                                      : 'bg-gray-100 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'
                                  } border`}
                                >
                                  {t(`status.${status}`)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>


                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('comic.adultContent')}
                        </label>
                        <div className="flex space-x-4">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="adult_content"
                              checked={formData.filter_criteria!.is_adult_content === undefined}
                              onChange={() => setFormData(prev => ({
                                ...prev,
                                filter_criteria: { ...prev.filter_criteria!, is_adult_content: undefined }
                              }))}
                              className="mr-2"
                            />
                            {t('common.any')}
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="adult_content"
                              checked={formData.filter_criteria!.is_adult_content === true}
                              onChange={() => setFormData(prev => ({
                                ...prev,
                                filter_criteria: { ...prev.filter_criteria!, is_adult_content: true }
                              }))}
                              className="mr-2"
                            />
                            {t('list.adult')} Only
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="adult_content"
                              checked={formData.filter_criteria!.is_adult_content === false}
                              onChange={() => setFormData(prev => ({
                                ...prev,
                                filter_criteria: { ...prev.filter_criteria!, is_adult_content: false }
                              }))}
                              className="mr-2"
                            />
                            {t('list.safe')} Only
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>

        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.name}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? t('common.saving') : isEditMode ? t('listEditor.updateList') : t('listEditor.createList')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListEditor;